import * as React from 'react';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import {
	useLazyPatientBasicInfoQuery,
	useLazyWorkplacePatientsQuery,
} from '../../../../graphql/graphql';
import { useAppContext } from '../../../../Services/AppContext';
import { FieldError } from 'react-hook-form';
import DynamicSelect from '../../../../Components/Base/DynamicSelect/DynamicSelect';
import { useEffectAsync } from '../../../../helpers';
import { useState } from 'react';
import useWorkplaces from '../../../Workplace/Hooks/useWorkplaces';
import PatientSelectItem from './PatientSelectItem';

interface PatientSelectProps {
	onChange?: (value: SelectValue) => void;
	workplaceId?: string;
	value?: SelectValue;
	error?: FieldError;
	placeholder?: string;
	searchInAllWorkplaces?: boolean;
	useBirthNumber?: boolean;
	useOriginalId?: boolean;
}

export interface PatientSelectValue extends SelectValue {
	item: any;
}

const PatientSelect: React.FC<PatientSelectProps> = props => {
	const {
		workplaceId,
		value,
		onChange,
		searchInAllWorkplaces = false,
		useBirthNumber = false,
		useOriginalId = false,
	} = props;
	const [defaultValue, setDefaultValue] = useState<SelectValue | undefined>(
		value,
	);
	const [searchPatients] = useLazyWorkplacePatientsQuery();
	const [getPatientInfo] = useLazyPatientBasicInfoQuery();
	const { userData } = useAppContext();
	const { workplaceIds } = useWorkplaces();

	const handleChange = (val: any) => {
		setDefaultValue(val);
		onChange?.(val);
	};

	let currentWorkplaceId: string;
	if (!workplaceId) {
		currentWorkplaceId = userData.workplaceId;
	} else {
		currentWorkplaceId = workplaceId;
	}

	useEffectAsync(async () => {
		if (value?.value && !value?.label) {
			const { user } = await getPatientInfo({
				id: value.value,
			}).unwrap();
			if (user?.profile?.fullName) {
				let val = user.id;
				if (useOriginalId && user?.originalId) {
					val = user.originalId;
				}
				if (useBirthNumber && user?.profile?.birthNumber) {
					val = user.profile.birthNumber;
				}
				const newValue = {
					value: val,
					label: user.profile.fullName,
				};
				setDefaultValue(newValue);
				onChange?.(newValue);
			}
		} else {
			setDefaultValue(undefined);
		}
	}, [value?.value]);

	const handleSearchItem = async (
		searchInput: string,
	): Promise<SelectValue[]> => {
		const { workplacePatients } = await searchPatients({
			fulltext: searchInput,
			workplaceIds: searchInAllWorkplaces ? workplaceIds : [currentWorkplaceId],
		}).unwrap();

		const items: PatientSelectValue[] = [];

		for (const workplacePatient of workplacePatients?.data || []) {
			if (workplacePatient && workplacePatient.user) {
				let newUserId = workplacePatient.user.id;
				if (useOriginalId && workplacePatient.user.originalId) {
					newUserId = workplacePatient.user.originalId;
				}
				if (useBirthNumber && workplacePatient.user?.profile?.birthNumber) {
					newUserId = workplacePatient.user.profile.birthNumber;
				}
				if (!items.some(item => item.value === newUserId)) {
					items.push({
						value: newUserId,
						label: workplacePatient.user.profile?.fullName || '',
						item: workplacePatient,
					});
				}
			}
		}

		return items;
	};

	return (
		<DynamicSelect
			searchItems={handleSearchItem}
			{...props}
			onChange={handleChange}
			value={defaultValue?.label ? defaultValue : value}
			ItemTemplate={PatientSelectItem}
		/>
	);
};

export default PatientSelect;
